/**
 * Jobs Service
 * Handles job/task management operations
 */

import api from './api';

class JobsService {
  /**
   * Get all jobs with filters
   * @param {Object} filters - Filter options
   */
  async getJobs(filters = {}) {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.date) params.append('date', filters.date);
    if (filters.driver) params.append('driver', filters.driver);
    if (filters.company) params.append('company', filters.company);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.order) params.append('order', filters.order);

    const response = await api.get(`/job?${params.toString()}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch jobs');
  }

  /**
   * Get job by ID
   * @param {string} jobId - Job ID
   */
  async getJobById(jobId) {
    const response = await api.get(`/job/${jobId}`);

    if (response.success && response.data) {
      return response.data.job || response.data;
    }

    throw new Error('Failed to fetch job');
  }

  /**
   * Create new job
   * @param {Object} jobData - Job data
   */
  async createJob(jobData) {
    const response = await api.post('/job', jobData);

    if (response.success && response.data) {
      return response.data.job || response.data;
    }

    throw new Error('Failed to create job');
  }

  /**
   * Update job
   * @param {string} jobId - Job ID
   * @param {Object} updates - Updated data
   */
  async updateJob(jobId, updates) {
    const response = await api.put(`/job/${jobId}`, updates);

    if (response.success && response.data) {
      return response.data.job || response.data;
    }

    throw new Error('Failed to update job');
  }

  /**
   * Delete job
   * @param {string} jobId - Job ID
   */
  async deleteJob(jobId) {
    const response = await api.delete(`/job/${jobId}`);

    if (response.success) {
      return true;
    }

    throw new Error('Failed to delete job');
  }

  /**
   * Assign driver to job with optional scheduling
   * @param {string} jobId - Job ID
   * @param {Object} assignmentData - Assignment data including driver and time windows
   * @param {string} assignmentData.driverId - Driver ID (required)
   * @param {string} assignmentData.pickupDate - Pickup date (optional)
   * @param {Object} assignmentData.pickupWindow - Pickup time window { start, end } (optional)
   * @param {string} assignmentData.deliveryDate - Delivery date (optional)
   * @param {Object} assignmentData.deliveryWindow - Delivery time window { start, end } (optional)
   */
  async assignDriver(jobId, assignmentData) {
    // Support both old format (just driverId string) and new format (object with time windows)
    const payload = typeof assignmentData === 'string'
      ? { driverId: assignmentData }
      : assignmentData;

    const response = await api.patch(`/job/${jobId}/assign`, payload);

    if (response.success && response.data) {
      return response.data.job || response.data;
    }

    throw new Error('Failed to assign driver');
  }

  /**
   * Update job status
   * @param {string} jobId - Job ID
   * @param {string} status - New status
   * @param {Object} options - Additional options (notes, location, exceptionReason)
   */
  async updateStatus(jobId, status, options = {}) {
    const response = await api.patch(`/job/${jobId}/status`, { status, ...options });

    if (response.success && response.data) {
      return response.data.job || response.data;
    }

    throw new Error(response.error?.message || 'Failed to update status');
  }

  /**
   * Approve job (Admin only)
   * @param {string} jobId - Job ID
   */
  async approveJob(jobId) {
    const response = await api.patch(`/job/${jobId}/approve`);

    if (response.success && response.data) {
      return response.data.job || response.data;
    }

    throw new Error(response.error?.message || 'Failed to approve job');
  }

  /**
   * Cancel job
   * @param {string} jobId - Job ID
   * @param {string} reason - Cancellation reason (optional)
   */
  async cancelJob(jobId, reason = '') {
    const response = await api.patch(`/job/${jobId}/cancel`, { reason });

    if (response.success && response.data) {
      return response.data.job || response.data;
    }

    throw new Error(response.error?.message || 'Failed to cancel job');
  }

  /**
   * Get job statistics
   */
  async getStats() {
    const response = await api.get('/job/stats');

    if (response.success && response.data) {
      return response.data.stats || response.data;
    }

    throw new Error('Failed to fetch job stats');
  }

  /**
   * Get today's jobs
   */
  async getTodaysJobs() {
    const response = await api.get('/job/today');

    if (response.success && response.data) {
      return response.data.jobs || response.data;
    }

    throw new Error('Failed to fetch today\'s jobs');
  }

  /**
   * Get a driver's optimized route for a given date.
   * Backend will call Google Directions and return stops in optimal order.
   * @param {string} driverId
   * @param {Object} options
   * @param {string} [options.date] - ISO date string (defaults to today)
   * @param {number} [options.startLat]
   * @param {number} [options.startLng]
   */
  async getDriverOptimizedRoute(driverId, { date, startLat, startLng } = {}) {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (Number.isFinite(startLat)) params.append('startLat', startLat);
    if (Number.isFinite(startLng)) params.append('startLng', startLng);

    const qs = params.toString();
    const response = await api.get(
      `/job/driver/${driverId}/optimized-route${qs ? `?${qs}` : ''}`,
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to fetch optimized route');
  }

  /**
   * Export jobs to CSV
   * @param {Object} filters - Filter options
   */
  async exportToCSV(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);

    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/job/export/csv?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${api.getToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return await response.blob();
  }

  /**
   * Export jobs to Excel
   * @param {Object} filters - Filter options
   */
  async exportToExcel(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);

    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/job/export/excel?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${api.getToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return await response.blob();
  }
  /**
   * Get the printable QR label for a job (same image the client portal prints).
   * @param {string} jobId
   * @returns {Promise<{qrCode: string, qrImageUrl: string, trackingUrl: string}>}
   */
  async generateQRCode(jobId) {
    const response = await api.post('/qr/generate', { jobId });

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to generate QR code');
  }

}

const jobsService = new JobsService();
export default jobsService;
